import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';

const milestones = [
  { year: '2022年', event: '91网页浏览器正式立项，核心团队组建完成，开启浏览器引擎自研之路。' },
  { year: '2023年', event: 'Velo渲染引擎1.0发布，91浏览器v1.0内测版上线，首批种子用户突破10万。' },
  { year: '2024年', event: 'v2.0正式版发布，新增广告拦截和隐私保护功能，全球用户突破1000万。' },
  { year: '2025年', event: '移动端版本发布，实现跨设备数据同步，荣获年度最佳浏览器工具奖。' },
  { year: '2026年', event: 'v4.2版本发布，搭载Velo引擎2.0，全球用户突破5000万，覆盖200+国家和地区。' },
];

const teamValues = [
  {
    icon: 'ri-focus-3-line',
    title: '用户至上',
    description: '每一个功能设计都从用户实际需求出发，追求极致的用户体验。',
  },
  {
    icon: 'ri-shield-star-line',
    title: '安全第一',
    description: '将用户数据安全与隐私保护放在首位，采用业界领先的加密技术。',
  },
  {
    icon: 'ri-rocket-2-line',
    title: '技术创新',
    description: '持续投入浏览器核心引擎研发，保持技术领先优势。',
  },
];

export default function AboutPage() {
  const orgFullSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91网页浏览器',
    url: 'https://www.91browser.com',
    description: '91网页浏览器是一款专为用户打造的极速、安全、稳定网页浏览工具。成立于2022年，全球用户超过5000万。',
    foundingDate: '2022',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@91browser.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgFullSchema) }}
      />

      <SEOHead
        title="关于我们 - 91网页浏览器"
        description="91网页浏览器成立于2022年，是一款专注于极速、安全、稳定的网页浏览工具。了解我们的品牌故事、发展历程和核心团队。"
        keywords="关于91浏览器,91浏览器品牌,浏览器开发团队,91浏览器发展历程"
        canonicalUrl="https://www.91browser.com/about"
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100/50">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-16 md:py-24">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block px-3.5 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold font-label mb-4">
                  品牌故事
                </span>
                <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-6">
                  为极速而生，为安全护航
                </h1>
                <div className="text-foreground-600 leading-relaxed space-y-4 text-sm">
                  <p>
                    91网页浏览器诞生于一个简单的愿景：让每个人都能享受极速、安全、无干扰的上网体验。
                    在这个信息爆炸的时代，浏览器的速度和安全性变得前所未有的重要。
                  </p>
                  <p>
                    <strong>我们组建了一支由浏览器引擎专家、安全工程师和产品设计师组成的核心团队</strong>，
                    从零开始研发Velo渲染引擎。经过四年的持续迭代，91浏览器已经成为全球5000万用户的共同选择。
                  </p>
                  <p>
                    我们的使命从未改变 —— 用技术的力量，为每一位用户打造一个更快、更安全、更纯粹的互联网窗口。
                    无论您是在工作中查阅资料、在学习中搜索知识，还是在闲暇时浏览资讯，
                    91网页浏览器都将是您最可靠的伙伴。
                  </p>
                </div>
              </div>

              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20tech%20office%20workspace%20with%20warm%20natural%20lighting%2C%20clean%20aesthetic%20with%20wooden%20desk%20and%20laptop%2C%20soft%20earthy%20tones%2C%20professional%20yet%20inviting%20atmosphere%2C%20editorial%20photography%20style%2C%20bright%20and%20airy%20composition&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="91网页浏览器团队办公环境"
                  title="91网页浏览器 - 办公环境"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="py-16 md:py-20 bg-background-100/70">
          <div className="px-4 md:px-6 max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                我们的理念
              </h2>
              <p className="text-foreground-600 text-sm">驱动我们不断前行的核心价值观</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {teamValues.map((value) => (
                <div
                  key={value.title}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/60 text-center"
                >
                  <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                    <i className={`${value.icon} text-accent-600 text-2xl w-7 h-7 flex items-center justify-center`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-16 md:py-24">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3.5 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold font-label mb-4">
                发展历程
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                一路走来的足迹
              </h2>
            </div>

            <div className="relative">
              <div className="absolute left-5 top-0 bottom-0 w-px bg-background-300"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((milestone, index) => (
                  <div key={index} className="flex items-start gap-6 relative">
                    <div className="relative z-10 w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-background-50 text-xs font-bold font-label">{milestone.year.slice(0, 4)}</span>
                    </div>
                    <div className="pt-1">
                      <h3 className="font-heading font-semibold text-foreground-900 mb-1">
                        {milestone.year}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {milestone.event}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-16 md:py-24 bg-background-100/70">
          <div className="px-4 md:px-6 max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block px-3.5 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-semibold font-label mb-4">
                联系我们
              </span>
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                期待与您沟通
              </h2>
              <p className="text-foreground-600 text-sm max-w-lg mx-auto">
                如有任何问题、建议或合作意向，欢迎通过以下方式联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/60 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-mail-line text-primary-600 text-xl w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm">电子邮箱</h3>
                <p className="text-sm text-foreground-600">support@91browser.com</p>
              </div>

              <div className="p-6 rounded-xl bg-background-50 border border-background-200/60 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className="ri-phone-line text-accent-600 text-xl w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm">客服电话</h3>
                <p className="text-sm text-foreground-600">400-888-9191</p>
              </div>

              <div className="p-6 rounded-xl bg-background-50 border border-background-200/60 text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-secondary-100 flex items-center justify-center">
                  <i className="ri-map-pin-line text-secondary-600 text-xl w-5 h-5 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading font-semibold text-foreground-900 mb-2 text-sm">公司地址</h3>
                <p className="text-sm text-foreground-600">北京市海淀区中关村科技园区</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}